/*global fetch */
import { mapValues, find, has } from "lodash";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  ref,
  off,
  get,
  onValue,
  query,
  equalTo,
  orderByChild,
} from "firebase/database";
import { httpsCallable } from "firebase/functions";

import { db, auth } from "./../firebase_config";
import { enqueueSnackbar } from "./notifications";
import { fetchRecentTransactions } from "./user";
import errorReporter from "../utils/errorReporting";

export const processSquarePayment = createAsyncThunk(
  "payment/processSquarePayment",
  async (
    { sourceId, amount, currency, locationId, idToken, note, customerId },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const response = await fetch(
        "https://us-central1-shelby-estates-hoa.cloudfunctions.net/processSquarePayment",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            sourceId,
            amount,
            currency,
            locationId,
            customerId,
            note,
          }),
        },
      );
      const data = await response.json();
      if (response.ok && data.success) {
        // await push(ref(db, `transactions/${data.userId}`), {
        //   ...data,
        // });
        dispatch(fetchRecentTransactions(data.userId));
        return data;
      } else {
        return rejectWithValue(data.error || "Payment processing failed");
      }
    } catch (error) {
      console.error("Error in processSquarePayment:", error);
      return rejectWithValue(
        "An unexpected error occurred while processing your payment",
      );
    }
  },
);

export const postSinglePayment = createAsyncThunk(
  "payment/postSinglePayment",
  async (transactionId, { dispatch, rejectWithValue }) => {
    try {
      const user = auth.currentUser;
      const idToken = await user.getIdToken();
      const response = await fetch(
        "https://us-central1-shelby-estates-hoa.cloudfunctions.net/postSinglePayment",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            transactionId,
          }),
        },
      );
      const data = await response.json();
      if (response.ok && data.success) {
        return data;
      } else {
        return rejectWithValue(data.error || "Payment posting failed");
      }
    } catch (error) {
      errorReporter.reportPaymentError(error, 'manual_post', {
        transaction_id: transactionId
      });
      console.error("Error in postSinglePayment:", error);
      return rejectWithValue(
        "An unexpected error occurred while posting your payment",
      );
    }
  },
);

export const fetchPaymentTransactionsByCustomer = createAsyncThunk(
  "payment/fetchPaymentTransactionsByCustomer",
  async (customerId, { dispatch, rejectWithValue }) => {
    try {
      // const users = await get(ref(db, "users")).then((s) => s.val());
      // const userAccounts = await get(ref(db, "userAccounts")).then((s) =>
      //   s.val(),
      // );
      // const accounts = await get(ref(db, "accounts")).then((s) => s.val());
      const sqRef = ref(db, "squareTransactions");
      await onValue(
        query(sqRef, orderByChild("customerId"), equalTo(customerId)),
        (snapshot) => {
          const txns = snapshot.val();
          return dispatch(listPayments(txns));
        },
      );
    } catch (error) {
      console.error("Error in fetchinSquarePaymentsByCustomer:", error);
    }
  },
);

export const fetchPaymentTransactions = createAsyncThunk(
  "payment/fetchPaymentTransactions",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const user = auth.currentUser;
      const idToken = await user.getIdToken();
      fetch(
        "https://us-central1-shelby-estates-hoa.cloudfunctions.net/fetchAllTransactions",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
        },
      ).then((response) => {
        response.json().then((d) => console.log(d));
      });
      // const data = await res.json();
      // console.log(data);
      const users = await get(ref(db, "users")).then((s) => s.val());
      const userAccounts = await get(ref(db, "userAccounts")).then((s) =>
        s.val(),
      );
      const accounts = await get(ref(db, "accounts")).then((s) => s.val());
      const postedPayments = await get(ref(db, `postedPayments`)).then((s) =>
        s.val(),
      );

      await onValue(ref(db, "squareTransactions"), (snapshot) => {
        const txns = snapshot.val();
        const payments = mapValues(txns, (txn) => {
          const { customerId } = txn;
          const posted = has(postedPayments, txn.id);
          const linked = find(
            users,
            (user) => user.squareCustomerId === customerId,
          );
          return {
            ...txn,
            firstName: linked?.firstName,
            lastName: linked?.lastName,
            posted,
          };
        });
        return dispatch(listPayments(payments));
      });
    } catch (error) {
      console.error("Error in fetchinSquarePayments:", error);
    }
  },
);

export const unfetchPaymentTransactions = createAsyncThunk(
  "payment/fetchPaymentTransactions",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      await off(ref(db, "squareTransactions"));
    } catch (error) {
      console.error("Error in unfetchinSquarePayments:", error);
    }
  },
);

// Auto-posting functionality
export const enableAutoPosting = createAsyncThunk(
  "payment/enableAutoPosting",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const user = auth.currentUser;
      const idToken = await user.getIdToken();
      
      const response = await fetch(
        "https://us-central1-shelby-estates-hoa.cloudfunctions.net/enableAutoPosting",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
        },
      );
      
      const data = await response.json();
      if (response.ok && data.success) {
        dispatch(
          enqueueSnackbar({
            message: "Auto-posting enabled. Completed transactions will be posted automatically.",
            options: { variant: "success" },
          }),
        );
        return data;
      } else {
        return rejectWithValue(data.error || "Failed to enable auto-posting");
      }
    } catch (error) {
      errorReporter.reportError(error, {
        component: 'payments',
        action: 'enable_auto_posting',
        severity: 'high'
      });
      console.error("Error enabling auto-posting:", error);
      return rejectWithValue(
        "An unexpected error occurred while enabling auto-posting",
      );
    }
  },
);

export const disableAutoPosting = createAsyncThunk(
  "payment/disableAutoPosting",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const user = auth.currentUser;
      const idToken = await user.getIdToken();
      
      const response = await fetch(
        "https://us-central1-shelby-estates-hoa.cloudfunctions.net/disableAutoPosting",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
        },
      );
      
      const data = await response.json();
      if (response.ok && data.success) {
        dispatch(
          enqueueSnackbar({
            message: "Auto-posting disabled. Manual posting is now required.",
            options: { variant: "info" },
          }),
        );
        return data;
      } else {
        return rejectWithValue(data.error || "Failed to disable auto-posting");
      }
    } catch (error) {
      errorReporter.reportError(error, {
        component: 'payments',
        action: 'disable_auto_posting',
        severity: 'medium'
      });
      console.error("Error disabling auto-posting:", error);
      return rejectWithValue(
        "An unexpected error occurred while disabling auto-posting",
      );
    }
  },
);

// Batch post completed payments
export const batchPostPayments = createAsyncThunk(
  "payment/batchPostPayments",
  async (transactionIds, { dispatch, rejectWithValue }) => {
    try {
      const user = auth.currentUser;
      const idToken = await user.getIdToken();
      
      const response = await fetch(
        "https://us-central1-shelby-estates-hoa.cloudfunctions.net/batchPostPayments",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ transactionIds }),
        },
      );
      
      const data = await response.json();
      if (response.ok && data.success) {
        dispatch(
          enqueueSnackbar({
            message: `Successfully posted ${data.postedCount} transactions.`,
            options: { variant: "success" },
          }),
        );
        return data;
      } else {
        return rejectWithValue(data.error || "Failed to batch post payments");
      }
    } catch (error) {
      errorReporter.reportPaymentError(error, 'batch_post', {
        transaction_count: transactionIds?.length || 0
      });
      console.error("Error batch posting payments:", error);
      return rejectWithValue(
        "An unexpected error occurred while batch posting payments",
      );
    }
  },
);

// Check auto-posting status
export const checkAutoPostingStatus = createAsyncThunk(
  "payment/checkAutoPostingStatus",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const user = auth.currentUser;
      const idToken = await user.getIdToken();
      
      const response = await fetch(
        "https://us-central1-shelby-estates-hoa.cloudfunctions.net/checkAutoPostingStatus",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
        },
      );
      
      const data = await response.json();
      if (response.ok && data.success) {
        return data;
      } else {
        return rejectWithValue(data.error || "Failed to check auto-posting status");
      }
    } catch (error) {
      // Don't report this as an error since it's a background check
      console.warn("Error checking auto-posting status:", error);
      return rejectWithValue(
        "Failed to check auto-posting status",
      );
    }
  },
);

const paymentSlice = createSlice({
  name: "payment",
  initialState: {
    loading: false,
    error: null,
    lastPaymentId: null,
    payments: {},
    autoPostingEnabled: false,
    autoPostingStatus: null,
    batchOperations: {
      posting: false,
      lastBatchResult: null
    }
  },
  reducers: {
    listPayments: (state, action) => {
      state.error = null;
      state.payments = { ...action.payload };
    },
    clearPaymentError: (state) => {
      state.error = null;
    },
    clearTxnId: (state) => {
      state.lastPaymentId = null;
    },
    setAutoPostingStatus: (state, action) => {
      state.autoPostingEnabled = action.payload.enabled;
      state.autoPostingStatus = action.payload.status;
    },
    clearBatchResult: (state) => {
      state.batchOperations.lastBatchResult = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(processSquarePayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(processSquarePayment.fulfilled, (state, action) => {
        state.loading = false;
        state.lastPaymentId = action.payload.paymentId;
      })
      .addCase(processSquarePayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(postSinglePayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(postSinglePayment.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(postSinglePayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Auto-posting reducers
      .addCase(enableAutoPosting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(enableAutoPosting.fulfilled, (state, action) => {
        state.loading = false;
        state.autoPostingEnabled = true;
        state.autoPostingStatus = action.payload.status;
      })
      .addCase(enableAutoPosting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(disableAutoPosting.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(disableAutoPosting.fulfilled, (state, action) => {
        state.loading = false;
        state.autoPostingEnabled = false;
        state.autoPostingStatus = action.payload.status;
      })
      .addCase(disableAutoPosting.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Batch posting reducers
      .addCase(batchPostPayments.pending, (state) => {
        state.batchOperations.posting = true;
        state.error = null;
      })
      .addCase(batchPostPayments.fulfilled, (state, action) => {
        state.batchOperations.posting = false;
        state.batchOperations.lastBatchResult = action.payload;
      })
      .addCase(batchPostPayments.rejected, (state, action) => {
        state.batchOperations.posting = false;
        state.error = action.payload;
      })
      // Auto-posting status check
      .addCase(checkAutoPostingStatus.fulfilled, (state, action) => {
        state.autoPostingEnabled = action.payload.enabled;
        state.autoPostingStatus = action.payload.status;
      });
  },
});

export const { 
  clearPaymentError, 
  clearTxnId, 
  listPayments, 
  setAutoPostingStatus,
  clearBatchResult 
} = paymentSlice.actions;

export default paymentSlice.reducer;
